import type { Column, Header, RowData, Table } from '@tanstack/table-core';
import type { CSSProperties, MaybeRefOrGetter } from 'vue';
import { useElementBounding } from '@vueuse/core';
import { computed, toValue } from 'vue';

export interface ResizeHandlePlacement {
	/** Zero-based visible column index; the handle shares this subgrid track. */
	index: number;
	/** Pin side of the column the handle resizes, or `false` for center columns. */
	pinned: false | 'left' | 'right';
	/** Frozen width of the column, used to place the left-pinned handle at its trailing edge. */
	columnWidth: number;
	/** Sticky offset of the column's pinned edge, resolved against the frozen widths. */
	pinnedOffset: number;
}

export interface ResizeIndicatorBounds {
	left: number;
	right: number;
}

export const TextDirections = {
	Ltr: 'ltr',
	Rtl: 'rtl',
} as const;

export type TextDirection = typeof TextDirections[keyof typeof TextDirections];

export function resizeIndicatorLeft(bounds: ResizeIndicatorBounds, direction: TextDirection): number {
	return direction === TextDirections.Rtl ? bounds.left : bounds.right;
}

/**
 * Pure placement for a boundary resize handle. Center columns ride their subgrid
 * track; pinned columns stick to their boundary (offset + width on the leading
 * side, offset on the trailing side) so the handle tracks the cell during
 * horizontal scroll instead of scrolling away.
 */
export function resizeHandleStyle({ index, pinned, columnWidth, pinnedOffset }: ResizeHandlePlacement): CSSProperties {
	const style: CSSProperties = {
		gridColumn: String(index + 1),
		gridRow: '1',
		// A pinned handle sits above its own sticky cell (z-1) so its boundary stays
		// grabbable. A center handle stays in the scrolling layer, below the pinned
		// cells, otherwise it scrolls under a frozen column and its hover line pokes
		// through the pinned column.
		zIndex: pinned ? 3 : 0,
	};

	if (pinned === 'left') {
		style.position = 'sticky';
		style.insetInlineStart = `${pinnedOffset + columnWidth}px`;
	}
	else if (pinned === 'right') {
		style.position = 'sticky';
		style.insetInlineEnd = `${pinnedOffset}px`;
	}

	return style;
}

export interface DataTableColumnResizeOptions<T extends RowData> {
	/** The TanStack table instance. */
	table: Table<T>;
	/** Visible header objects in render order. */
	visibleColumns: MaybeRefOrGetter<Header<T, unknown>[]>;
	/** The fill column id; it gets no resize handle since it flexes to fill remaining space. */
	fillColumnId: MaybeRefOrGetter<string | undefined>;
	/** Frozen column widths keyed by column id. */
	resolvedColumnWidths: MaybeRefOrGetter<Record<string, number>>;
	/** Resolves a pinned column's sticky offset against the frozen widths. */
	resolvedPinnedOffset: (column: Column<T>, side: 'left' | 'right') => number;
	/** Header `<tr>` element, used to read the live `<th>` rect while resizing. */
	headerElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
	/** Scroll root the resize indicator is absolutely positioned in. */
	rootElement: MaybeRefOrGetter<Element | null | undefined>;
	/** Reactive scroll offsets of `rootElement`; keeps the indicator in view while scrolling. */
	rootScroll: MaybeRefOrGetter<{ x: number; y: number }>;
	/** Fit a column to its full content width as an uncapped manual width (double click). */
	onAutoFitColumn?: (columnId: string) => void;
}

/**
 * Owns the column resize-handle interaction: which boundaries are resizable,
 * where each handle sits, starting a resize, resetting a column to auto width,
 * and the drag indicator overlay. Lives outside the table component so the
 * placement math stays unit-testable in isolation (see `resizeHandleStyle`).
 */
export function useDataTableColumnResize<T extends RowData>(options: DataTableColumnResizeOptions<T>) {
	const { table } = options;
	const visibleColumns = computed(() => toValue(options.visibleColumns));
	const fillColumnId = computed(() => toValue(options.fillColumnId));
	const resolvedColumnWidths = computed(() => toValue(options.resolvedColumnWidths));
	const headerElement = computed(() => toValue(options.headerElement));

	function canColumnResize(column: Column<T>): boolean {
		return column.id !== fillColumnId.value && column.getCanResize();
	}

	function getResizeHandleStyle(header: Header<T, unknown>, index: number): CSSProperties {
		const pinned = header.column.getIsPinned();

		return resizeHandleStyle({
			index,
			pinned,
			columnWidth: resolvedColumnWidths.value[header.id] ?? header.column.getSize(),
			pinnedOffset: pinned ? options.resolvedPinnedOffset(header.column, pinned) : 0,
		});
	}

	/**
	 * Start a resize from the boundary handle. A double click arrives as a second
	 * `mousedown` (detail > 1); swallow it so the dbl-click reset runs instead of
	 * starting another resize.
	 */
	function onResizeMouseDown(event: MouseEvent, header: Header<T, unknown>): void {
		if (event.detail > 1) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		header.getResizeHandler()(event);
	}

	/**
	 * Fit a column to its full content on double click. Clearing the manual width
	 * first lets the column render in its auto track while the auto-fit measures
	 * the uncapped content width, which is then persisted as a new manual width.
	 */
	function onResizeDoubleClick(header: Header<T, unknown>): void {
		// `prev` is typed non-nullable by TanStack but is `undefined` at runtime
		// until a manual width exists (the sizing state is a Vue model); the guard
		// is load-bearing, not dead code.
		table.setColumnSizing((prev) => {
			if (!prev) return {};
			const { [header.id]: _omitted, ...rest } = prev;
			return rest;
		});

		options.onAutoFitColumn?.(header.id);
	}

	const resizingColumn = computed(() => {
		const resizingColumnId = table.getState().columnSizingInfo?.isResizingColumn;
		return resizingColumnId ? table.getColumn(resizingColumnId as string) : undefined;
	});

	const resizingColumnElBounding = useElementBounding(() => {
		if (!resizingColumn.value || !headerElement.value) return null;

		const columnIndex = visibleColumns.value.findIndex((header) => header.id === resizingColumn.value!.id);
		if (columnIndex === -1) return null;

		return headerElement.value.querySelectorAll('th')[columnIndex];
	});

	const resizeIndicatorInsets = computed<CSSProperties | null>(() => {
		const root = toValue(options.rootElement);
		if (!resizingColumn.value || !root) return null;

		const direction = headerElement.value && getComputedStyle(headerElement.value).direction === TextDirections.Rtl
			? TextDirections.Rtl
			: TextDirections.Ltr;

		const boundary = resizeIndicatorLeft({
			left: resizingColumnElBounding.left.value,
			right: resizingColumnElBounding.right.value,
		}, direction);

		const scroll = toValue(options.rootScroll);
		const rootPaddingLeft = root.getBoundingClientRect().left + root.clientLeft;
		const left = boundary - rootPaddingLeft + scroll.x;

		return {
			left: `${left}px`,
			top: `${scroll.y}px`,
			height: `${root.clientHeight}px`,
		};
	});

	return {
		canColumnResize,
		getResizeHandleStyle,
		onResizeMouseDown,
		onResizeDoubleClick,
		resizeIndicatorInsets,
	};
}
